import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

interface DetailsContent {
  weddingDate: string;
  venueName: string;
  venueDescription: string;
  venueAddress: string;
  venueCity: string;
  venueMapUrl: string;
}

interface TimelineEvent {
  time: string;
  title: string;
  description: string;
}

const Details: React.FC = () => {
  const [content, setContent] = useState<DetailsContent>({
    weddingDate: 'Sunday, September 6th, 2026',
    venueName: 'Jefferson Beach Marina',
    venueDescription: 'The wedding will be held at the Jefferson Beach Marina on the shores of Lake St. Clair.',
    venueAddress: '24400 Jefferson Avenue',
    venueCity: 'Saint Clair Shores, Michigan',
    venueMapUrl: 'https://www.google.com/maps/search/?api=1&query=Jefferson+Beach+Marina+24400+Jefferson+Avenue+Saint+Clair+Shores+Michigan'
  });
  const [timeline, setTimeline] = useState<TimelineEvent[]>([
    { time: '10:30 AM', title: 'Guests Arrive',        description: 'Guests are encouraged to arrive at the marina' },
    { time: '11:00 AM', title: 'Ceremony',              description: 'Ceremony begins on the waterfront' },
    { time: '11:30 AM', title: 'Board the Ovation Yacht', description: 'Board the Ovation Yacht for the reception' },
    { time: '12:00 PM', title: 'Cruise Departs',        description: 'The yacht leaves promptly for a cruise on Lake St. Clair' },
    { time: '3:00 PM',  title: 'Return to Dock',        description: 'The yacht returns to the marina' },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const detailsDoc = await getDoc(doc(db, 'siteContent', 'details'));
        const timelineDoc = await getDoc(doc(db, 'siteContent', 'timeline'));
        if (detailsDoc.exists()) setContent(detailsDoc.data() as DetailsContent);
        if (timelineDoc.exists() && timelineDoc.data().events) setTimeline(timelineDoc.data().events);
      } catch (e) {
        console.error('Error fetching details:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <main>
      <div className="page-header">
        <p className="page-header__eyebrow">Loading…</p>
        <h1 className="page-header__title">Wedding Details</h1>
      </div>
    </main>
  );

  return (
    <main>
      {/* PAGE HEADER */}
      <div className="page-header">
        <AnchorSVG className="page-header__anchor" />
        <p className="page-header__eyebrow">September 6, 2026</p>
        <h1 className="page-header__title">Wedding Details</h1>
        <div className="page-header__rule page-rule">
          <div className="rule-line rule-line--short"></div>
          <div className="rule-diamond"></div>
          <div className="rule-line rule-line--short"></div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="details-content">
        <div className="details-inner">

          {/* VENUE */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Venue</p>
            <div className="venue-card">
              <p className="venue-card__desc">{content.venueDescription}</p>
              <p className="venue-card__name">{content.venueName}</p>
              <p className="venue-card__address">
                {content.venueAddress}<br/>
                {content.venueCity}
              </p>
              <a className="venue-card__link" href={content.venueMapUrl} target="_blank" rel="noopener noreferrer">
                View on Map →
              </a>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule">
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* TIMELINE */}
          <div className="detail-section">
            <p className="section-eyebrow detail-section__eyebrow">Day of Timeline</p>

            {timeline.map((event, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-time">{event.time}</div>
                <div className="timeline-body-col">
                  <div className="timeline-dot-col">
                    <div className="timeline-dot"></div>
                    {index < timeline.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-body">
                    <div className="timeline-title">{event.title}</div>
                    <div className="timeline-desc">{event.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="detail-note">
            <p>
              <strong>Please note:</strong> The ceremony promptly begins at 11:00 AM.
              We kindly ask that all guests arrive by 10:30 AM.
              Reception to follow on the water.
            </p>
          </div>

        </div>
      </div>
    </main>
  );
};

export default Details;
